import { ReactNode, useCallback, useRef, useState } from "react";
import { VersionCheckContext, VersionCheckContextType } from "./VersionCheckContext";

const POLL_INTERVAL_MS = 60_000;          // re-check every 60 s
const SNOOZE_DURATION_MS = 5 * 60_000;    // "remind later" = 5 min

/** Simulated network call — returns a newer version after a short delay.
 *  Replace the body with:  return (await (await fetch("/api/version")).json()).version;
 */
async function fetchRemoteVersion(): Promise<string> {
  return new Promise((resolve) => {
    setTimeout(() => resolve("2.5.0"), 4000); // pretend server responds in 4 s
  });
}

/** Compare two semver strings. Returns  1 if a > b,  0 if equal, -1 if a < b. */
function compareSemver(a: string, b: string): number {
  const pa = a.split(".").map(Number);
  const pb = b.split(".").map(Number);
  for (let i = 0; i < 3; i++) {
    if ((pa[i] || 0) > (pb[i] || 0)) return 1;
    if ((pa[i] || 0) < (pb[i] || 0)) return -1;
  }
  return 0;
}

interface VersionCheckProviderProps {
  children: ReactNode;
  currentVersion: string;
}

export const VersionCheckProvider: React.FC<VersionCheckProviderProps> = ({ children, currentVersion }) => {
  const [status, setStatus] = useState<"idle" | "checking" | "updateAvailable" | "upToDate">("idle");
  const [remoteVersion, setRemoteVersion] = useState("");
  const [dismissed, setDismissed] = useState(false);
  const snoozedUntil = useRef(0);

  const dismiss = useCallback(() => {
    setDismissed(true);
  }, []);

  const snooze = useCallback(() => {
    snoozedUntil.current = Date.now() + SNOOZE_DURATION_MS;
    setStatus("idle");
  }, []);

  const receiveVersion = useCallback((version: string) => {
    if (dismissed) return;
    console.log("Received version:", version, "current:", currentVersion);
    setRemoteVersion(version);
    setStatus(compareSemver(version, currentVersion) > 0 ? "updateAvailable" : "upToDate");
  }, [dismissed, currentVersion]);

  const value: VersionCheckContextType = {
    status,
    currentVersion,
    remoteVersion,
    dismiss,
    snooze,
    receiveVersion,
  };

  return (
    <VersionCheckContext.Provider value={value}>
      {children}
    </VersionCheckContext.Provider>
  );
};

